import { useEffect, useState } from "react";
import axios from "axios";
import { Bot, Layers, Loader2, Volume2 } from "lucide-react";

const apiUrl = process.env.REACT_APP_BACKEND_URL || "";

/** What answers a number: an agent pinned to one version, and a voice. */
export interface Assignment {
  agent_id: string;
  version_id: string;
  voice: string;
}

interface AgentSummary {
  agent_id: string;
  agent_name?: string;
  name?: string;
  id?: string;
}

interface AgentVersion {
  version_id: string;
  version_name?: string;
}

interface VoiceOptionItem {
  local_voice_name: string;
  name?: string;
  gender?: string;
}

const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

/** Agents, their versions and the account's voices, with the current choice. */
export function useAssignment(token: string | null | undefined, initial: Partial<Assignment> | null | undefined) {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [versions, setVersions] = useState<AgentVersion[]>([]);
  const [voices, setVoices] = useState<VoiceOptionItem[]>([]);
  const [value, setValue] = useState<Assignment>({
    agent_id: initial?.agent_id || "",
    version_id: initial?.version_id || "",
    voice: initial?.voice || "",
  });
  const [loading, setLoading] = useState(true);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) return;
      try {
        setLoading(true);
        const [agentsRes, voicesRes] = await Promise.all([
          axios.get(`${apiUrl}/api/v1/agents`, { params: { view: "summary" }, ...auth(token) }),
          axios.get(`${apiUrl}/api/v1/voice/options`, auth(token)),
        ]);
        if (cancelled) return;
        const rawAgents: AgentSummary[] = Array.isArray(agentsRes.data)
          ? agentsRes.data
          : Array.isArray(agentsRes.data?.agents)
            ? agentsRes.data.agents
            : [];
        const rawVoices: VoiceOptionItem[] = Array.isArray(voicesRes.data?.voices)
          ? voicesRes.data.voices
          : Array.isArray(voicesRes.data)
            ? voicesRes.data
            : [];
        setAgents(rawAgents);
        setVoices(rawVoices);
        setValue((v) => ({
          ...v,
          agent_id: v.agent_id || rawAgents[0]?.agent_id || rawAgents[0]?.id || "",
          voice: v.voice || rawVoices[0]?.local_voice_name || "",
        }));
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.detail || "Failed to load agent and voice options.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    async function loadVersions() {
      if (!token || !value.agent_id) {
        setVersions([]);
        return;
      }
      try {
        setLoadingVersions(true);
        const agentUrl = `${apiUrl}/api/v1/agents/${encodeURIComponent(value.agent_id)}`;
        // The summary view has no versions list, which left the client dropdown empty.
        const [res, versionsRes] = await Promise.all([
          axios.get(agentUrl, { params: { view: "summary" }, ...auth(token) }),
          axios.get(`${agentUrl}/versions/summary`, { params: { limit: 200 }, ...auth(token) }),
        ]);
        if (cancelled) return;
        const list: AgentVersion[] = Array.isArray(versionsRes.data) ? versionsRes.data : [];
        setVersions(list);
        // A number is pinned to one version; preselect the agent's current one.
        setValue((v) => ({
          ...v,
          version_id:
            v.version_id && list.some((item) => item.version_id === v.version_id)
              ? v.version_id
              : res.data?.current_version || list[0]?.version_id || "",
        }));
      } catch {
        if (!cancelled) setVersions([]);
      } finally {
        if (!cancelled) setLoadingVersions(false);
      }
    }
    loadVersions();
    return () => {
      cancelled = true;
    };
  }, [token, value.agent_id]);

  const complete = Boolean(value.agent_id && value.version_id && value.voice);
  return { agents, versions, voices, value, setValue, loading, loadingVersions, error, complete };
}

const SELECT =
  "w-full h-10 px-3 text-sm rounded-lg border border-line bg-paper text-ink focus:outline-none focus:ring-2 focus:ring-brand";

export function AssignmentFields({ idPrefix, assignment }: { idPrefix: string; assignment: ReturnType<typeof useAssignment> }) {
  const { agents, versions, voices, value, setValue, loadingVersions } = assignment;
  return (
    <>
      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-agent-select`} className="text-xs font-medium text-ink flex items-center gap-1.5">
          <Bot className="w-3.5 h-3.5 text-brand" />
          AI Receptionist Agent
          <span className="text-danger">*</span>
        </label>
        <select
          id={`${idPrefix}-agent-select`}
          data-testid={`${idPrefix}-agent-select`}
          value={value.agent_id}
          onChange={(e) => setValue({ ...value, agent_id: e.target.value, version_id: "" })}
          className={SELECT}
        >
          <option value="" disabled>Select an agent</option>
          {agents.map((agent) => {
            const id = agent.agent_id || agent.id || "";
            const name = agent.agent_name || agent.name || id;
            return (
              <option key={id} value={id}>
                {name} {id !== name ? `(${id})` : ""}
              </option>
            );
          })}
        </select>
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-version-select`} className="text-xs font-medium text-ink flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5 text-brand" />
          Agent Version
          <span className="text-danger">*</span>
          {loadingVersions && <Loader2 className="w-3 h-3 animate-spin text-ink-3" />}
        </label>
        <select
          id={`${idPrefix}-version-select`}
          data-testid={`${idPrefix}-version-select`}
          value={value.version_id}
          onChange={(e) => setValue({ ...value, version_id: e.target.value })}
          className={SELECT}
        >
          <option value="" disabled>Select a version</option>
          {versions.map((v) => (
            <option key={v.version_id} value={v.version_id}>
              {v.version_name || v.version_id}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-ink-3">
          The number stays on this version. Publishing a new version does not move it until you change it here.
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-voice-select`} className="text-xs font-medium text-ink flex items-center gap-1.5">
          <Volume2 className="w-3.5 h-3.5 text-brand" />
          Receptionist Voice
          <span className="text-danger">*</span>
        </label>
        <select
          id={`${idPrefix}-voice-select`}
          data-testid={`${idPrefix}-voice-select`}
          value={value.voice}
          onChange={(e) => setValue({ ...value, voice: e.target.value })}
          className={SELECT}
        >
          <option value="" disabled>Select a voice</option>
          {voices.map((voice) => (
            <option key={voice.local_voice_name} value={voice.local_voice_name}>
              {voice.name || voice.local_voice_name}
              {voice.gender ? ` (${voice.gender})` : ""}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
